import { isValid, parseISO } from 'date-fns';
import { injectable } from 'inversify';

import { getMediaStatusFromEventState } from '../utils/liveEvent';
import { createURL } from '../utils/urlFormatting';
import { getDataOrThrow } from '../utils/api';
import { filterMediaOffers } from '../utils/entitlements';
import { useConfigStore as ConfigStore } from '../stores/ConfigStore';
import type { GetPlaylistParams, Playlist, PlaylistItem } from '../../types/playlist';
import type { ContentList } from '../../types/content-list';
import type { AdConfig, AdSchedule } from '../../types/ad-schedule';
import type { EpisodeInSeries, EpisodesRes, EpisodesWithPagination, GetSeriesParams, Series } from '../../types/series';
import env from '../env';
import { logError } from '../logger';
import type { Config } from '../../types/config';
import { APP_CONFIG_ITEM_TYPE } from '../constants';
import { isEpisode } from '../utils/media';
import { isTruthyCustomParamValue } from '../utils/common';

// change the values below to change the property used to look up the alternate image
enum ImageProperty {
  CARD = 'card',
  BACKGROUND = 'background',
  CHANNEL_LOGO = 'channel_logo',
}

type ContentApiServiceArgs<T> = T & { config: Config };

const PAGE_LIMIT = 20;

@injectable()
export default class ContentApiService {
  protected BASE_URL = env.APP_CONTENT_API_BASE_URL;

  /**
   * We use playlistLabel prop to define the label used for all media items inside.
   * That way we can change the behavior of the same media items being in different playlists
   */
  protected generateAlternateImageURL = ({ mediaId, label, playlistLabel }: { mediaId: string; label: string; playlistLabel?: string }) => {
    const pathname = `/v2/media/${mediaId}/images/${playlistLabel || label}.webp`;
    const url = createURL(`${this.BASE_URL}${pathname}`, {
      poster_fallback: 1,
      fallback: playlistLabel ? label : null,
    });

    return url;
  };

  protected parseDate = (item: PlaylistItem, prop: string) => {
    const date = item[prop] as string | undefined;

    if (date && !isValid(new Date(date))) {
      logError('ContentApiService', `Invalid "${prop}" date provided for the "${item.title}" media item`, { error: new Error('Invalid date') });
      return undefined;
    }

    return date ? parseISO(date) : undefined;
  };

  protected getTranslatedFields = (item: PlaylistItem, language?: string) => {
    if (!language) {
      return item;
    }

    const defaultLanguage = env.APP_DEFAULT_LANGUAGE;
    const transformedItem = { ...item };

    if (language !== defaultLanguage) {
      const [languageCode, regionCode] = language.split('-');
      for (const [key, _] of Object.entries(transformedItem)) {
        if (!!regionCode && item[`${key}-${language}`]) {
          transformedItem[key] = item[`${key}-${language}`];
        } else if (item[`${key}-${languageCode}`]) {
          transformedItem[key] = item[`${key}-${languageCode}`];
        }
      }
    }

    return transformedItem;
  };

  /**
   * This method enhances episode playlist items in a playlist with additional information about the series and
   * episode meta (season/episode number).
   * It is optimised to use the watchList and series endpoints, but they support a maximum of 50 ids to resolve.
   * When there are more episodes, multiple requests are performed in batches.
   */
  protected enhancePlaylistItemEpisodes = async (config: Config, playlist: Playlist, language?: string): Promise<Playlist> => {
    const episodesInPlaylist = playlist.playlist.filter(isEpisode).map((item) => item.mediaid);
    const watchlistId = config.features?.continueWatchingList;

    // no episodes found or the watchlist is not configured
    if (!episodesInPlaylist.length || !watchlistId) return playlist;

    // series/watchlist requests are limited to 50 media_ids
    const batchCount = Math.ceil(episodesInPlaylist.length / 50);
    let series: Record<string, EpisodeInSeries[]> = {};
    let seriesMediaItems: Record<string, PlaylistItem> = {};

    for (let batch = 0; batch < batchCount; batch++) {
      const fetchedSeries = await this.getSeriesByMediaIds({ mediaIds: episodesInPlaylist.slice(batch * 50, (batch + 1) * 50), config });
      const mediaIds = [...new Set(Object.values(fetchedSeries || []).map((val) => val[0]?.series_id))].filter((id) => id && !seriesMediaItems[id]);
      const fetchedSeriesMediaItems = mediaIds.length ? await this.getMediaItemsByWatchList({ config, watchlistId, mediaIds, language }) : [];

      series = { ...series, ...fetchedSeries };
      seriesMediaItems = { ...seriesMediaItems, ...Object.fromEntries(fetchedSeriesMediaItems.map((item) => [item.mediaid, item])) };
    }

    // enhance the episode media items if we have found series metadata
    playlist.playlist = playlist.playlist.map((item) => {
      const seriesMetadata = series[item.mediaid]?.[0];
      const seriesMedia = seriesMediaItems[seriesMetadata?.series_id];

      if (isEpisode(item) && seriesMetadata && seriesMedia) {
        // these fields are already translated by the `transformMediaItem` function
        const title = seriesMedia.title;
        const subtitle = item.title;
        const episodeNumber = seriesMetadata.episode_number ? String(seriesMetadata.episode_number) : undefined;
        const seasonNumber = seriesMetadata.season_number ? String(seriesMetadata.season_number) : undefined;

        return {
          ...item,
          title,
          subtitle,
          episodeNumber,
          seasonNumber,
        };
      }

      return item;
    });

    return playlist;
  };

  /**
   * Transform incoming content lists
   */
  protected transformContentList = async (config: Config, contentList: ContentList, language: string): Promise<Playlist> => {
    const { list, ...rest } = contentList;

    const playlist: Playlist = { ...rest, playlist: [] };

    playlist.playlist = list.map((item) => {
      const { custom_params, media_id, description, tags, ...rest } = item;

      const playlistItem: PlaylistItem = {
        feedid: contentList.id,
        mediaid: media_id,
        tags: tags.join(','),
        description: description || '',
        sources: [],
        images: [],
        image: '',
        link: '',
        pubdate: 0,
        ...rest,
        ...custom_params,
      };

      return this.transformMediaItem({ item: playlistItem, playlist, language });
    });

    return await this.enhancePlaylistItemEpisodes(config, playlist, language);
  };

  /**
   * Transform incoming playlists
   */
  protected transformPlaylist = async (config: Config, playlist: Playlist, relatedMediaId?: string, language?: string) => {
    playlist.playlist = playlist.playlist.map((item) => this.transformMediaItem({ item, playlist, language }));

    // remove the related media item (when this is a recommendations playlist)
    if (relatedMediaId) {
      playlist.playlist = playlist.playlist.filter((item) => item.mediaid !== relatedMediaId);
    }

    return this.enhancePlaylistItemEpisodes(config, playlist, language);
  };

  /**
   * Transform incoming media items
   * - Parses productId into MediaProduct[] for all cleeng offers
   */
  protected transformMediaItem = ({ item, playlist, language }: { item: PlaylistItem; playlist?: Playlist; language?: string }) => {
    const config = ConfigStore.getState().config;
    const offerKeys = Object.keys(config?.integrations)[0];
    const playlistLabel = playlist?.imageLabel;
    const mediaId = item.mediaid;
    const translatedFields = this.getTranslatedFields(item, language);

    const transformedMediaItem = {
      ...item,
      ...translatedFields,
      cardImage: this.generateAlternateImageURL({ mediaId, label: ImageProperty.CARD, playlistLabel }),
      channelLogoImage: this.generateAlternateImageURL({ mediaId, label: ImageProperty.CHANNEL_LOGO, playlistLabel }),
      backgroundImage: this.generateAlternateImageURL({ mediaId, label: ImageProperty.BACKGROUND }),
      mediaOffers: item.productIds ? filterMediaOffers(offerKeys, item.productIds) : undefined,
      scheduledStart: this.parseDate(item, 'VCH.ScheduledStart'),
      scheduledEnd: this.parseDate(item, 'VCH.ScheduledEnd'),
    };

    if (typeof item.genre === 'string') {
      transformedMediaItem.genre = item.genre.split(',').join(', ');
    }

    // add the media status to the media item after the transformation because the live media status depends on the scheduledStart and scheduledEnd
    transformedMediaItem.mediaStatus = getMediaStatusFromEventState(transformedMediaItem);

    return transformedMediaItem;
  };

  protected transformEpisodes = (episodesRes: EpisodesRes, language?: string, seasonNumber?: number) => {
    const { episodes, page, page_limit, total } = episodesRes;

    // Adding images and keys for media items
    return {
      episodes: episodes
        .filter((el) => el.media_item)
        .map((el) => ({
          ...this.transformMediaItem({ item: el.media_item as PlaylistItem, language }),
          seasonNumber: seasonNumber?.toString() || el.season_number?.toString() || '',
          episodeNumber: String(el.episode_number),
        })),
      pagination: { page, page_limit, total },
    };
  };

  /**
   * Get watchlist by playlistId
   */
  protected getMediaItemsByWatchList = async ({
    config,
    watchlistId,
    mediaIds,
    token,
    language,
  }: {
    config: Config;
    watchlistId: string;
    mediaIds: string[];
    token?: string;
    language?: string;
  }): Promise<PlaylistItem[]> => {
    if (!mediaIds?.length) {
      return [];
    }

    const pathname = `/apps/watchlists/${watchlistId}`;
    const url = createURL(`${this.BASE_URL}${pathname}`, { token, media_ids: mediaIds });
    const response = await fetch(url);
    const data = (await getDataOrThrow(response)) as Playlist;

    if (!data) throw new Error(`The data was not found using the watchlist ${watchlistId}`);

    const transformed = await this.transformPlaylist(config, data, language);

    return transformed.playlist;
  };

  getWatchHistoryMediaItems = async ({
    ids,
    config,
    language,
    token,
  }: ContentApiServiceArgs<{
    ids: string[];
    language?: string;
    token?: string;
  }>) => {
    const watchlistId = config.features?.continueWatchingList;

    return watchlistId ? this.getMediaItemsByWatchList({ config, mediaIds: ids, language, token, watchlistId }) : [];
  };

  getFavoriteMediaItems = async ({
    ids,
    config,
    language,
    token,
  }: ContentApiServiceArgs<{
    ids: string[];
    language?: string;
    token?: string;
  }>) => {
    const watchlistId = config.features?.favoritesList;

    return watchlistId ? this.getMediaItemsByWatchList({ config, mediaIds: ids, language, token, watchlistId }) : [];
  };

  /**
   * Get media item
   */
  protected getMedia = async ({ id, token, drmPolicyId, language }: { id: string; token?: string; drmPolicyId?: string; language?: string }) => {
    const pathname = drmPolicyId && token ? `/v2/media/${id}/drm/${drmPolicyId}` : `/v2/media/${id}`;
    const url = createURL(`${this.BASE_URL}${pathname}`, { token });
    const response = await fetch(url);
    const data = (await getDataOrThrow(response)) as Playlist;
    const mediaItem = data.playlist[0];

    if (!mediaItem) throw new Error('MediaItem not found');

    return this.transformMediaItem({ item: mediaItem, language });
  };

  /**
   * Get signed media item
   */
  protected getMediaByIdWithToken = async ({
    id,
    siteId,
    token,
    drmPolicyId,
    language,
  }: {
    id: string;
    siteId: string;
    token: string;
    drmPolicyId?: string;
    language?: string;
  }) => {
    const url = createURL(`${this.BASE_URL}/v2/sites/${siteId}/media/${id}/playback.json`, { token, drm_policy_id: drmPolicyId });
    const response = await fetch(url);
    const data = (await getDataOrThrow(response)) as Playlist;
    const mediaItem = data.playlist[0];

    if (!mediaItem) throw new Error('MediaItem not found');

    return this.transformMediaItem({ item: mediaItem, language });
  };

  /**
   * Get media by id with passport
   */
  protected getMediaByIdWithPassport = async ({
    id,
    siteId,
    planId,
    passport,
    language,
  }: {
    id: string;
    siteId: string;
    planId: string;
    passport: string;
    language?: string;
  }): Promise<PlaylistItem> => {
    const pathname = `/v2/sites/${siteId}/media/${id}/playback.json`;
    const url = createURL(`${this.BASE_URL}${pathname}`, { passport, plan_id: planId });
    const response = await fetch(url);
    const data = (await getDataOrThrow(response)) as Playlist;
    const mediaItem = data.playlist[0];

    if (!mediaItem) throw new Error('MediaItem not found');

    return this.transformMediaItem({ item: mediaItem, language });
  };

  /**
   * Get media by id
   */
  getMediaById = async ({
    id,
    token,
    planId,
    passport,
    language,
    config,
  }: ContentApiServiceArgs<{
    id: string;
    token?: string;
    planId?: string;
    passport?: string;
    language?: string;
  }>): Promise<PlaylistItem> => {
    const drmPolicyId = (config.contentProtection?.drm?.defaultPolicyId || config.custom?.drmPolicyId) as string | undefined;

    if (planId && passport) {
      return this.getMediaByIdWithPassport({ id, planId, passport, language, siteId: config.siteId });
    }

    // when enabled, use the playback.json endpoint for fetching the media item
    const usePlaybackEndpoint = isTruthyCustomParamValue(config.custom?.usePlaybackEndpoint) && config.siteId;
    if (usePlaybackEndpoint && token) {
      return this.getMediaByIdWithToken({ id, siteId: config.siteId, token, drmPolicyId, language });
    }

    // used for public media OR as fallback to the "legacy" DRM media endpoints when the playback.json endpoint is not
    // enabled
    return this.getMedia({ id, language, drmPolicyId, token });
  };

  /**
   * Get series by id
   * @param {string} id
   * @param params
   */
  getSeries = async ({
    id,
    params = {},
  }: ContentApiServiceArgs<{
    id: string;
    params?: GetSeriesParams;
  }>): Promise<Series | undefined> => {
    if (!id) {
      throw new Error('Series ID is required');
    }

    const pathname = `/apps/series/${id}`;
    const url = createURL(`${this.BASE_URL}${pathname}`, params);
    const response = await fetch(url);

    return (await getDataOrThrow(response)) as Series;
  };

  /**
   * Get all series for the given media_ids
   */
  getSeriesByMediaIds = async ({
    mediaIds,
  }: ContentApiServiceArgs<{
    mediaIds: string[];
  }>): Promise<Record<string, EpisodeInSeries[]> | undefined> => {
    const pathname = `/apps/series`;
    const url = `${this.BASE_URL}${pathname}?media_ids=${mediaIds.join(',')}`;
    const response = await fetch(url);

    return (await getDataOrThrow(response)) as Record<string, EpisodeInSeries[]>;
  };

  /**
   * Get season of the selected series
   */
  protected getSeasonWithEpisodes = async ({
    seriesId,
    seasonNumber,
    pageOffset,
    pageLimit = PAGE_LIMIT,
    language,
  }: ContentApiServiceArgs<{
    seriesId: string | undefined;
    language: string;
    seasonNumber: number;
    pageOffset?: number;
    pageLimit?: number;
  }>): Promise<EpisodesWithPagination> => {
    if (!seriesId) {
      throw new Error('Series ID is required');
    }

    const pathname = `/apps/series/${seriesId}/seasons/${seasonNumber}/episodes`;
    const url = createURL(`${this.BASE_URL}${pathname}`, { page_offset: pageOffset, page_limit: pageLimit });

    const response = await fetch(url);
    const episodesRes = (await getDataOrThrow(response)) as EpisodesRes;

    return this.transformEpisodes(episodesRes, language, seasonNumber);
  };

  /**
   * Get all episodes of the selected series (when no particular season is selected or when episodes are attached to series)
   */
  getEpisodes = async ({
    seriesId,
    pageOffset,
    seasonNumber,
    pageLimit = PAGE_LIMIT,
    afterId,
    language,
    config,
  }: ContentApiServiceArgs<{
    seriesId: string | undefined;
    seasonNumber?: number;
    pageOffset?: number;
    pageLimit?: number;
    afterId?: string;
    language: string;
  }>): Promise<EpisodesWithPagination> => {
    if (!seriesId) {
      throw new Error('Series ID is required');
    }

    if (typeof seasonNumber === 'number') {
      // Get episodes from a selected season using pagination
      const season = await this.getSeasonWithEpisodes({
        seriesId,
        seasonNumber,
        pageOffset,
        language,
        config,
      });

      return { pagination: season.pagination, episodes: season.episodes };
    }

    const pathname = `/apps/series/${seriesId}/episodes`;
    const url = createURL(`${this.BASE_URL}${pathname}`, {
      page_offset: pageOffset,
      page_limit: pageLimit,
      after_id: afterId,
    });

    const response = await fetch(url);
    const episodesResponse = (await getDataOrThrow(response)) as EpisodesRes;

    return this.transformEpisodes(episodesResponse, language);
  };

  getAdSchedule = async ({
    mediaId,
    config,
    isLoggedIn,
    hasSubscription,
  }: ContentApiServiceArgs<{
    mediaId: string;
    isLoggedIn?: boolean;
    hasSubscription?: boolean;
  }>): Promise<AdSchedule | AdConfig | undefined> => {
    const { adSchedule: adScheduleId, adConfig: adConfigId, adScheduleUrls, adDeliveryMethod, custom } = config;

    // We use client side ads only when delivery method is not pointing at server ads
    // adConfig and adScheduled can't be enabled at the same time
    const useAdConfigFlow = !!adConfigId && adDeliveryMethod !== 'ssai';

    if (adScheduleId) {
      const url = this.BASE_URL + `/v2/advertising/schedules/${adScheduleId}.json`;
      const response = await fetch(url, { credentials: 'omit' });

      return (await getDataOrThrow(response)) as AdSchedule;
    }

    const disableAds = { client: 'vast', schedule: '' };
    if (useAdConfigFlow) {
      if (custom?.adConfigCondition === 'isNotLoggedIn' && isLoggedIn) {
        return disableAds;
      }
      if (custom?.adConfigCondition === 'isNotSubscribed' && hasSubscription) {
        return disableAds;
      }

      return {
        client: 'vast',
        schedule: createURL(adScheduleUrls?.xml || '', {
          media_id: mediaId,
        }),
      } as AdConfig;
    }

    return disableAds;
  };

  protected getPlaylist = async ({
    id,
    config,
    params = {},
    language = env.APP_DEFAULT_LANGUAGE,
  }: {
    config: Config;
    id: string;
    params?: GetPlaylistParams;
    language?: string;
  }) => {
    const pathname = `/v2/playlists/${id}`;
    const url = createURL(`${this.BASE_URL}${pathname}`, params);
    const response = await fetch(url);
    const data = (await getDataOrThrow(response)) as Playlist;

    return this.transformPlaylist(config, data, params.related_media_id, language);
  };

  protected getContentList = async ({ config, id, siteId, language }: { config: Config; id: string; siteId: string; language: string }): Promise<Playlist> => {
    if (!id || !siteId) {
      throw new Error('List ID and Site ID are required');
    }

    const pathname = `/v2/sites/${siteId}/content_lists/${id}`;
    const url = createURL(`${this.BASE_URL}${pathname}`, {});
    const response = await fetch(url);
    const data = (await getDataOrThrow(response)) as ContentList;

    return this.transformContentList(config, data, language);
  };

  /**
   * Get playlist by id
   */
  getPlaylistById = async ({
    type = 'playlist',
    id,
    config,
    params = {},
    language = env.APP_DEFAULT_LANGUAGE,
  }: ContentApiServiceArgs<{
    type: string;
    id: string;
    params?: GetPlaylistParams;
    language?: string;
  }>) => {
    if (type === APP_CONFIG_ITEM_TYPE.playlist) {
      return this.getPlaylist({ config, id, params, language });
    }

    const siteId = config.siteId;
    if (type === APP_CONFIG_ITEM_TYPE.content_list && siteId) {
      return this.getContentList({ config, id, siteId, language });
    }

    throw new Error(`Failed to get playlist with id ${id}`);
  };

  search = async ({ searchTerm, config }: ContentApiServiceArgs<{ searchTerm: string }>) => {
    const searchPlaylist = config.features?.searchPlaylist;
    const searchParams = config.custom?.searchParams as string | undefined;

    if (searchPlaylist) {
      return this.getPlaylistById({
        type: 'playlist',
        id: searchPlaylist,
        params: { search: searchTerm, use_tags: 'true', use_params: searchParams },
        language: 'en',
        config,
      });
    }

    throw new Error('Search is not supported');
  };
}
